import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useApi } from "../utils/Api";
import foodImage from "../assets/hero-image.jpeg";

const WHATSAPP_NUMBER = "447465682308";

// Spinner overlay
const SpinnerOverlay = () => (
  <div className="spinner-overlay">
    <div className="spinner"></div>
  </div>
);

export default function OrderTracking() {
  const { apiFetch } = useApi();
  const navigate = useNavigate();

  const [status, setStatus] = useState("to do");
  const [orderType, setOrderType] = useState("");
  const [orderNumber, setOrderNumber] = useState("");
  const [orderId, setOrderId] = useState("");
  const [loading, setLoading] = useState(true);

  const [searchParams] = useSearchParams();
  const encryptedId = searchParams.get("id");

  useEffect(() => {
    if (!encryptedId) {
      navigate("/", { replace: true });
      return;
    }
    let interval;

    const fetchStatus = async () => {
      try {
        const data = await apiFetch("order-status", {
          encrypted_order_id: encryptedId,
        });

        if (!data?.success || !data?.order) {
          navigate("/menu", { replace: true });
          return;
        }

        const order = data.order;

        if (!order.status) {
          navigate("/menu", { replace: true });
          return;
        }

        const s = String(order.status ?? "").toLowerCase();

        setStatus(order.status);
        setOrderType(order.order_type);
        setOrderNumber(order.order_number);
        setOrderId(order.id);
        setLoading(false);

        if (s === "cancelled" || s === "completed") clearInterval(interval);
      } catch (err) {
        console.error("Failed to fetch order:", err);
        navigate("/menu", { replace: true });
      }
    };

    fetchStatus();
    interval = setInterval(fetchStatus, 10000);

    return () => clearInterval(interval);
  }, [encryptedId, apiFetch, navigate]);

  const isDelivery = String(orderType || "").toLowerCase() === "delivery";

  const steps = [
    { key: "to do", label: "Order Received", subtext: "Your order is in queue" },
    {
      key: "in progress",
      label: "Being Prepared",
      subtext: "Your order is being prepared.",
    },
    {
      key: "completed",
      label: isDelivery ? "Ready for Delivery" : "Order Ready",
      subtext: isDelivery
        ? "Your order is ready to be delivered."
        : "Your order is ready for collection.",
    },
  ];

  const normalizedStatus = String(status || "").toLowerCase();
  const stepOrder = { "to do": 0, "in progress": 1, completed: 2 };
  const currentStep = stepOrder[normalizedStatus] ?? 0;

  const progressMap = {
    "to do": "w-1/5",
    "in progress": "w-3/5",
    completed: "w-full",
  };

  // Show spinner while fetching
  if (loading) return <SpinnerOverlay />;

  // Cancelled order UI
  if (normalizedStatus === "cancelled") {
    return (
      <div className="min-h-screen bg-black text-white px-5 pt-12 pb-10">
        <div className="mx-auto w-full max-w-[420px]">
          <header className="rounded-2xl border border-red-400/30 bg-red-500/10 p-6">
            <div className="flex items-center gap-3">
              <span className="text-red-300 text-xl">✖</span>
              <h1 className="text-lg font-extrabold text-red-200">
                Order Cancelled
              </h1>
            </div>
            <p className="mt-2 text-sm text-red-100">
              Unfortunately, your order was cancelled.
            </p>
          </header>

          <p className="mt-4 text-xs uppercase tracking-widest text-white/60">
            Order <span className="text-white">#{orderNumber}</span>
          </p>
          <p className="text-xs text-white/50 mt-2">
            Ref: <span className="text-white">{orderId}</span>
          </p>

          <div className="mt-4 rounded-xl border border-red-400/40 bg-red-500/10 p-4 text-center text-sm font-semibold text-red-200">
            This order will not be processed.
          </div>

          <a
            href={`https://wa.me/${WHATSAPP_NUMBER}`}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-extrabold text-white hover:bg-white/10 active:scale-[0.98]"
          >
            <span>💬</span> Click to chat with us on WhatsApp
          </a>

          <div className="mt-6 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03]">
            <img src={foodImage} alt="Order" className="w-full" />
          </div>
        </div>
      </div>
    );
  }

  // Normal order tracking UI
  return (
    <div className="min-h-screen bg-black text-white px-5 pt-12 pb-10">
      <div className="mx-auto w-full max-w-[420px]">
        <header className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-xl font-extrabold">
              {steps[currentStep]?.label}
            </h1>
            <p className="mt-1 text-sm text-white/60">
              {steps[currentStep]?.subtext}
            </p>
          </div>

          <Link
            to="/menu"
            className="shrink-0 rounded-xl bg-white/10 px-3 py-2 text-xs font-bold text-white hover:bg-white/15 active:scale-[0.98]"
          >
            Back to Menu
          </Link>
        </header>

        <div className="mt-6">
          <div className="h-3 w-full overflow-hidden rounded-full bg-white/15">
            <div
              className={`h-full rounded-full bg-green-500 transition-all duration-500 ${
                progressMap[normalizedStatus] || "w-1/5"
              }`}
            />
          </div>
        </div>

        <p className="mt-4 text-xs uppercase tracking-widest text-white/60">
          Order <span className="text-white">#{orderNumber}</span>
        </p>
        <p className="text-xs text-white/50 mt-2">
          Ref: <span className="text-white">{orderId}</span>
        </p>

        <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.03] p-4">
          {steps.map((step, index) => {
            const active = index <= currentStep;
            return (
              <div
                key={step.key}
                className={`flex items-start gap-3 py-3 ${
                  index !== steps.length - 1 ? "border-b border-white/10" : ""
                } ${active ? "opacity-100" : "opacity-50"}`}
              >
                <span
                  className={`mt-1 h-2.5 w-2.5 rounded-full ${
                    active ? "bg-green-500" : "bg-white/30"
                  }`}
                />
                <div className="min-w-0">
                  <p className="text-sm font-semibold">{step.label}</p>
                  <p className="mt-1 text-xs text-white/60">{step.subtext}</p>
                </div>
              </div>
            );
          })}
        </div>

        {normalizedStatus === "completed" && (
          <div className="mt-4 rounded-2xl bg-green-500 p-4 text-center text-sm font-extrabold text-black">
            {isDelivery ? "Your order is ready for delivery." : "Your order is ready."}
          </div>
        )}

        <a
          href={`https://wa.me/${WHATSAPP_NUMBER}`}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-6 flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-extrabold text-white hover:bg-white/10 active:scale-[0.98]"
        >
          <span>💬</span> Click to chat with us on WhatsApp
        </a>



        <div className="mt-6 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03]">
          <img src={foodImage} alt="Order" className="w-full" />
        </div>
      </div>
    </div>
  );
}